// src/components/vault/ui/transitions/AnimatedContent.tsx
import { AnimatePresence, motion } from "motion/react";
import { useRef, type ReactNode } from "react";

interface AnimatedContentProps {
  children: ReactNode;
  contentKey: string;
  direction?: number; // kept for API compatibility
  className?: string;
}

/**
 * Pure fade transition — 150ms opacity-only.
 * Skips animation on the very first render to avoid a flash on mount.
 * Respects prefers-reduced-motion via MotionConfig in VaultLayout.
 */
export function AnimatedContent({
  children,
  contentKey,
  className,
}: AnimatedContentProps) {
  const isFirstRender = useRef(true);
  const prevKeyRef = useRef(contentKey);

  if (prevKeyRef.current !== contentKey) {
    isFirstRender.current = false;
    prevKeyRef.current = contentKey;
  }

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={contentKey}
        initial={isFirstRender.current ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12, ease: "easeOut" }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Alias for consistency
export const FadeContent = AnimatedContent;

interface TabStepContentProps {
  children: ReactNode;
  tabKey: string;
  stepKey: string;
  direction?: number; // kept for API compatibility
  className?: string;
}

/**
 * Tab/step transitions — subtle 150ms opacity-only fade.
 * Skips animation on the very first render to avoid a flash when the vault
 * page mounts and the initial requirement/tab is set.
 * Respects prefers-reduced-motion via MotionConfig in VaultLayout.
 */
export function TabStepContent({
  children,
  tabKey,
  stepKey,
  className,
}: TabStepContentProps) {
  const combinedKey = `${tabKey}-${stepKey}`;

  // Track whether this is the very first key we've seen.
  // On the first render we show content instantly (no slide/fade).
  const isFirstRender = useRef(true);
  const prevKeyRef = useRef(combinedKey);

  if (prevKeyRef.current !== combinedKey) {
    isFirstRender.current = false;
    prevKeyRef.current = combinedKey;
  }

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={combinedKey}
        initial={isFirstRender.current ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12, ease: "easeOut" }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
